import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaMapMarkerAlt, FaPhone, FaStar } from "react-icons/fa";

const VillaDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const hotel = state?.hotel;

  // Safe array converter
  const toArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return value.split(",").map((v) => v.trim());
      }
    }

    if (typeof value === "object") {
      return Object.values(value).map(String);
    }

    return [String(value)];
  };

  if (!hotel) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white p-10 rounded-xl shadow-xl text-center max-w-lg">
          <h2 className="text-3xl font-bold text-red-600">Villa Not Found</h2>
          <p className="mt-4 text-gray-600">
            Sorry, we couldn't find the villa you're looking for.
          </p>

          <button
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto p-6">

        {/* Close Button */}
        <button
          onClick={() => navigate(state?.from || "/", { replace: true })}
          className="flex items-center text-indigo-600 hover:text-indigo-800 mb-6"
        >
          <FaArrowLeft className="mr-2" />
          Close
        </button>

        {/* Main Card */}
        <div className="bg-white shadow-2xl rounded-2xl overflow-hidden">

          {/* Banner Image */}
          <div className="relative">
            <img
              src={hotel.image}
              alt={hotel.name}
              className="w-full h-[420px] object-cover rounded-b-2xl"
            />
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-4 py-2 rounded-xl text-sm">
              {hotel.provider}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">

            {/* Title */}
            <h2 className="text-4xl font-bold text-gray-900">
              {hotel.name}
            </h2>

            {/* Location */}
            <p className="flex items-center text-lg text-gray-600 mt-2">
              <FaMapMarkerAlt className="mr-2 text-green-600" />
              {hotel.location}
            </p>

            {/* Rating + Tag */}
            <div className="flex items-center mt-4">
              <span className="flex items-center text-yellow-600 font-semibold text-lg">
                <FaStar className="mr-1" />
                {hotel.rating}
              </span>

              <span className="ml-4 text-gray-500">
                ({hotel.reviewCount} reviews)
              </span>

              {hotel.tag && (
                <span className="ml-4 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                  {hotel.tag}
                </span>
              )}
            </div>

            {/* Price */}
            <p className="text-3xl font-bold text-green-700 mt-5">
              ₹{hotel.pricePerNight ?? hotel.price}
              <span className="text-lg text-gray-500"> / night</span>
            </p>

            {/* Description */}
            <div className="mt-6 text-gray-700 text-lg leading-relaxed">
              {hotel.Description}
            </div>

            {/* Amenities */}
            <div className="mt-8">
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Amenities</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-600">
                {toArray(hotel?.amenities).map((item, i) => (
                  <li key={i} className="flex items-center">• {item}</li>
                ))}
              </ul>
            </div>

            {/* Perks */}
            <div className="mt-8">
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Perks</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-600">
                {toArray(hotel?.perks).map((perk, i) => (
                  <li key={i} className="flex items-center">• {perk}</li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="mt-8 flex items-center text-lg text-gray-700">
              <FaPhone className="mr-3 text-indigo-600" />
              <strong>Contact:</strong>&nbsp;
              {hotel.contactNumber || "Not available"}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default VillaDetails;
